import { SanitizedHtmlRenderer } from '@/components/html.render'
import Modal from '@/components/modal'
import { PolicySourceUrlViewer } from '@/components/policy/policy-source-url-viewer'
import { Button } from '@/components/ui/button'
import { PolicyGenerationMethod } from '@/enum/global.enum'
import type { IPolicy } from '@/interface/policy.interface'
import { formatFileSize, getFileIcon, getFileTypeColor } from '@/utils/file-utils'
import { Download, Eye, FileText, Mail } from 'lucide-react'
import { useEffect, useState } from 'react'
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer'

export interface IAcknowledgementPolicyFile {
  url: string
  name: string
  version: number
  mimeType: string
  size: number
  id: string
  isLatest: boolean
}

export interface IPolicyOwner {
  name: string
  email: string | null
  position: string | null
}

export type IAcknowledgementPolicy = Omit<IPolicy, 'files'> & {
  policyHistoryId?: string
  files: IAcknowledgementPolicyFile[]
  policyOwner: IPolicyOwner
}

const MIME_TO_EXTENSION: Record<string, string> = {
  jpeg: 'jpg',
  msword: 'doc',
  'vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'vnd.ms-excel': 'xls',
  'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'vnd.ms-powerpoint': 'ppt',
  'vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
}

// Helper function to get fileType for DocViewer
const getFileType = (file: IAcknowledgementPolicyFile | null): string => {
  if (!file) return ''

  // Try to get extension from filename first
  const fileExtension = file.name?.split('.').pop()?.toLowerCase()
  if (fileExtension) return fileExtension

  // Extract extension from MIME type (e.g., "image/png" -> "png")
  if (file.mimeType) {
    const mimeParts = file.mimeType.split('/')
    if (mimeParts.length === 2) {
      const mimeExtension = mimeParts[1].split(';')[0].trim() // Handle cases like "image/png; charset=utf-8"
      return MIME_TO_EXTENSION[mimeExtension] || mimeExtension
    }
  }

  // Fallback to MIME type itself (react-doc-viewer supports MIME types too)
  return file.mimeType
}

export const PolicyOwnerDetails = ({ owner }: { owner?: IPolicyOwner | null }) => {
  if (!owner?.name) return null

  return (
    <div className='mt-3 sm:mt-4 flex items-center gap-3'>
      <div className='min-w-0'>
        <p className='text-[10px] font-medium uppercase tracking-wide text-muted-foreground'>Policy Owner</p>
        <div className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
          <span className='text-xs sm:text-sm font-medium'>{owner.name}</span>
          {owner.position && <span className='text-xs text-muted-foreground'>· {owner.position}</span>}
        </div>
        {owner.email && (
          <a
            href={`mailto:${owner.email}`}
            className='flex items-center gap-1 text-[11px] sm:text-xs text-primary hover:underline break-all'
          >
            <Mail className='h-3 w-3 shrink-0' />
            {owner.email}
          </a>
        )}
      </div>
    </div>
  )
}

export const PolicyAcknowledgementContent = ({ policy }: { policy: IAcknowledgementPolicy }) => {
  const [fileLoading, setFileLoading] = useState(false)
  const [openFileViewer, setOpenFileViewer] = useState(false)
  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<IAcknowledgementPolicyFile | null>(null)

  // File type detection
  const isImage = selectedFile?.mimeType?.startsWith('image/')
  const isPdf = selectedFile?.mimeType === 'application/pdf'

  // For images and PDFs, we can use direct rendering (no CORS issues)
  // For other files, we'll try DocViewer which may work for some file types
  useEffect(() => {
    if (openFileViewer && selectedFile?.url) {
      // Images and PDFs can be loaded directly without fetch
      if (isImage || isPdf) {
        setFileBlobUrl(selectedFile.url)
        setFileLoading(false)
      } else {
        // For other file types, try to fetch as blob (may fail due to CORS)
        loadFileAsBlob()
      }
    } else {
      // Clean up blob URL when modal closes
      if (fileBlobUrl && !fileBlobUrl.startsWith('http')) URL.revokeObjectURL(fileBlobUrl)

      setFileBlobUrl(null)
      setFileLoading(false)
    }

    return () => {
      // Cleanup on unmount
      if (fileBlobUrl && !fileBlobUrl.startsWith('http')) URL.revokeObjectURL(fileBlobUrl)
    }
  }, [openFileViewer, selectedFile])

  const loadFileAsBlob = async () => {
    if (!selectedFile?.url) return

    try {
      setFileLoading(true)
      // For non-image/PDF files, try to fetch as blob
      // This will fail if CORS is not configured, but we'll fallback to direct URL
      try {
        const response = await fetch(selectedFile.url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
        })

        if (!response.ok) throw new Error('Failed to fetch file')

        const blob = await response.blob()
        const blobUrl = URL.createObjectURL(blob)
        setFileBlobUrl(blobUrl)
      } catch (fetchError) {
        // CORS error - use direct URL and let DocViewer try to handle it
        // Some file types might work with iframe-based rendering
        console.warn('CORS error, using direct URL:', fetchError)
        setFileBlobUrl(selectedFile.url)
      }
    } catch (error) {
      console.error('Error loading file:', error)
      setFileBlobUrl(selectedFile.url)
    } finally {
      setFileLoading(false)
    }
  }

  return (
    <>
      <div className='space-y-4 mx-auto  min-w-0'>
        {/* File Attachments - Simplified */}
        {policy.files && policy.files.length > 0 && (
          <div>
            <h2 className='text-sm sm:text-base font-semibold mb-2 sm:mb-3 flex items-center gap-1'>
              <Download className='h-3.5 w-3.5 sm:h-4 sm:w-4 mb-[2px]' />
              Policy Files ({policy.files.length})
            </h2>
            <div className='space-y-2'>
              {policy.files.map((file) => {
                const FileIcon = getFileIcon(file.mimeType)
                return (
                  <div
                    key={file.id}
                    className='flex items-center justify-between gap-2 p-2 border rounded-md hover:bg-muted/20 transition-colors'
                  >
                    <div className='flex min-w-0 items-center gap-2 sm:gap-3'>
                      <div className='relative shrink-0'>
                        <FileIcon className={`h-6 w-6 sm:h-8 sm:w-8 ${getFileTypeColor(file.mimeType)}`} />
                        <div className='absolute -top-1 -right-1.5 w-4 h-4 sm:w-5 sm:h-5 bg-primary rounded-full flex items-center justify-center'>
                          <span className='text-primary-foreground text-[8px] sm:text-[10px] font-medium'>
                            v{file.version ?? 1}
                          </span>
                        </div>
                      </div>
                      <div className='min-w-0'>
                        <p className='font-medium text-xs sm:text-sm truncate'>{file.name}</p>
                        <div className='flex items-center space-x-1 text-[10px] text-muted-foreground'>
                          <FileText className='h-[10px] w-[10px]' />
                          <span>{formatFileSize(file.size)}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => {
                        setSelectedFile(file)
                        setOpenFileViewer(true)
                      }}
                      className='flex shrink-0 items-center gap-1 h-7 px-2 text-xs'
                      aria-label={`View ${file.name}`}
                    >
                      <Eye className='h-3 w-3' />
                      <span className='hidden sm:inline'>View</span>
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {/* Policy Description - Simplified */}
        {policy?.description &&
          (policy?.generationMethod === PolicyGenerationMethod.MANUAL ||
            policy?.generationMethod === PolicyGenerationMethod.AI) && (
            <div className='text-sm sm:text-base overflow-x-auto break-words [&_table]:text-xs sm:[&_table]:text-sm [&_img]:max-w-full [&_h1]:text-lg sm:[&_h1]:text-2xl [&_h2]:text-base sm:[&_h2]:text-xl [&_h3]:text-sm sm:[&_h3]:text-lg'>
              <SanitizedHtmlRenderer htmlContent={policy?.description ?? ''} />
            </div>
          )}

        {policy?.sourceUrl && policy?.generationMethod === PolicyGenerationMethod.URL ? (
          <PolicySourceUrlViewer url={policy?.sourceUrl} minHeight='1000px' />
        ) : null}
      </div>

      <Modal size='xl' open={openFileViewer} setOpen={setOpenFileViewer} title={selectedFile?.name ?? ''}>
        <div className='h-[calc(85dvh-100px)] sm:h-[calc(80vh-100px)] overflow-hidden'>
          {fileLoading ? (
            <div className='flex items-center justify-center h-full'>
              <div className='text-center'>
                <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4'></div>
                <p className='text-muted-foreground'>Loading file...</p>
              </div>
            </div>
          ) : selectedFile?.url ? (
            <>
              {/* Render images directly - no CORS issues */}
              {isImage && (
                <div className='w-full h-full flex items-center justify-center bg-black/5 dark:bg-white/5 rounded-lg overflow-auto'>
                  <img
                    loading='lazy'
                    decoding='async'
                    src={selectedFile.url}
                    alt={selectedFile.name || 'Image'}
                    className='max-w-full max-h-full object-contain'
                  />
                </div>
              )}

              {/* Render PDFs directly using iframe - no CORS issues */}
              {isPdf && (
                <div className='w-full h-full'>
                  <iframe
                    src={`${selectedFile.url}#toolbar=1&navpanes=1&scrollbar=1`}
                    className='w-full h-full min-h-[60dvh] sm:min-h-[600px] border rounded-lg'
                    title={selectedFile.name || 'PDF Document'}
                  />
                </div>
              )}

              {/* Use DocViewer for other file types */}
              {!isImage && !isPdf && fileBlobUrl && (
                <DocViewer
                  documents={[
                    {
                      uri: fileBlobUrl,
                      fileType: getFileType(selectedFile),
                    },
                  ]}
                  pluginRenderers={DocViewerRenderers}
                  style={{ height: '100%' }}
                  config={{
                    header: {
                      disableHeader: true,
                      disableFileName: true,
                    },
                  }}
                />
              )}
            </>
          ) : (
            <div className='flex items-center justify-center h-full'>
              <div className='text-center'>
                <p className='text-muted-foreground mb-4'>Failed to load file</p>
                {selectedFile?.url && (
                  <Button variant='outline' onClick={() => window.open(selectedFile.url, '_blank')}>
                    Open in New Tab
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}
